import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { FREQUENCIES } from "@/lib/api";
import { PageContainer, PageHeader, PrimaryButton, SecondaryButton, EmptyState, inputCls } from "@/components/ui-kit";
import CustomerForm, { CLASSIFICATIONS } from "@/components/CustomerForm";
import ImportExcelModal from "@/components/ImportExcelModal";
import { toast } from "sonner";
import { Plus, Search, ArrowUpRight, Pencil, FileSpreadsheet, Receipt, MapPin, Trash2, CheckSquare, Square, AlertTriangle } from "lucide-react";

const freqLabel = (v) => FREQUENCIES.find((f) => f.value === v)?.label || v;
const CLASS_MAP = Object.fromEntries(CLASSIFICATIONS.map((c) => [c.value, c]));

function ClassificationBadge({ value, testid }) {
  const meta = CLASS_MAP[value] || CLASS_MAP.activo_contrato;
  return (
    <span
      data-testid={testid}
      className="font-mono-kpi text-[9px] uppercase tracking-wider px-1.5 py-0.5 border inline-flex items-center gap-0.5"
      style={{ color: meta.color, borderColor: `${meta.color}55`, background: `${meta.color}0d` }}
    >
      {meta.label}
    </span>
  );
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [selected, setSelected] = useState(new Set());
  const [alertMap, setAlertMap] = useState({});

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((c) => c.id)));
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`¿Eliminar ${ids.length} cliente(s) definitivamente? Esta acción borra TODO el historial asociado y no se puede deshacer.`)) return;
    const { data } = await api.post("/customers/bulk-delete", { ids });
    toast.success(`${data.deleted} cliente(s) eliminado(s)`);
    setSelected(new Set());
    load();
  };

  const bulkDeactivate = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`¿Dar de baja ${ids.length} cliente(s)? Se preserva el historial; podrás reactivarlos luego.`)) return;
    const { data } = await api.post("/customers/bulk-deactivate", { ids });
    toast.success(`${data.deactivated} cliente(s) dados de baja`);
    setSelected(new Set());
    load();
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (!showInactive) params.active = true;
    const [{ data }, alerts] = await Promise.all([
      api.get("/customers", { params }),
      api.get("/invoices/stats/customers-with-unpaid").then((r) => r.data).catch(() => ({})),
    ]);
    setItems(data);
    setAlertMap(alerts || {});
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, showInactive]);

  useEffect(() => { setSelected(new Set()); }, [q, showInactive]);

  const handleSubmit = async (payload) => {
    if (editing) {
      await api.patch(`/customers/${editing.id}`, payload);
      toast.success("Cliente actualizado");
    } else {
      await api.post("/customers", payload);
      toast.success("Cliente creado y contrato generado");
    }
    setEditing(null);
    load();
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const sorted = [...items].sort((a, b) => {
    const av = a[sortBy] || "";
    const bv = b[sortBy] || "";
    const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv), "es");
    return sortDir === "asc" ? cmp : -cmp;
  });

  const HeaderCell = ({ col, label, align = "left", width }) => (
    <button
      onClick={() => toggleSort(col)}
      style={{ width }}
      data-testid={`customers-sort-${col}`}
      className={`px-3 py-2.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-600 hover:text-[#FF5A00] flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}
    >
      {label}
      {sortBy === col && <span className="text-[#FF5A00]">{sortDir === "asc" ? "↑" : "↓"}</span>}
    </button>
  );

  return (
    <PageContainer>
      <PageHeader
        kicker={`${items.length} clientes`}
        title="Clientes"
        subtitle="Cartera completa con periodicidad, contactos y precio mensual en UF."
        actions={
          <div className="flex gap-2">
            <SecondaryButton data-testid="customers-import-button" onClick={() => setShowImport(true)}>
              <FileSpreadsheet className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Importar Excel
            </SecondaryButton>
            <PrimaryButton data-testid="customers-new-button" onClick={() => { setEditing(null); setShowForm(true); }}>
              <Plus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Nuevo cliente
            </PrimaryButton>
          </div>
        }
      />

      <div className="mb-6 flex items-center gap-4 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="customers-search-input"
            placeholder="Buscar por nombre, dirección o contacto…"
            className={`${inputCls} pl-10`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" data-testid="customers-show-inactive" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="w-4 h-4" />
          <span>Incluir clientes de baja</span>
        </label>
      </div>

      {loading ? (
        <div className="font-mono-kpi text-xs text-neutral-500 uppercase tracking-[0.2em]">Cargando…</div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Sin clientes"
          description="Agrega tu primer cliente para empezar."
          action={<PrimaryButton onClick={() => { setEditing(null); setShowForm(true); }}><Plus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Crear cliente</PrimaryButton>}
        />
      ) : (
        <div className="bg-white border border-neutral-200" data-testid="customers-list">
          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div data-testid="bulk-action-bar" className="flex items-center justify-between px-4 py-2 bg-[#0A0A0A] text-white">
              <div className="font-mono-kpi text-xs uppercase tracking-[0.15em]">
                {selected.size} seleccionado(s)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelected(new Set())}
                  className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-300 hover:text-white px-3 py-1.5"
                >
                  Deseleccionar
                </button>
                <button
                  data-testid="customers-bulk-deactivate"
                  onClick={bulkDeactivate}
                  className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] bg-[#FFC107] hover:bg-[#FFB300] text-[#0A0A0A] px-3 py-1.5 inline-flex items-center gap-1.5"
                >
                  Dar de baja en lote
                </button>
                <button
                  data-testid="customers-bulk-delete"
                  onClick={bulkDelete}
                  className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] bg-[#D50000] hover:bg-[#B71C1C] text-white px-3 py-1.5 inline-flex items-center gap-1.5"
                >
                  <Trash2 className="w-3 h-3" /> Eliminar
                </button>
              </div>
            </div>
          )}

          {/* Header row */}
          <div className="grid grid-cols-[40px_2.2fr_1fr_1.6fr_0.9fr_0.7fr_0.7fr_60px] gap-2 border-b border-neutral-200 bg-neutral-50">
            <button
              type="button"
              data-testid="customers-select-all"
              onClick={toggleAll}
              className="flex items-center justify-center py-2.5 text-neutral-500 hover:text-[#FF5A00]"
              title={selected.size === items.length ? "Deseleccionar todos" : "Seleccionar todos"}
            >
              {selected.size === items.length && items.length > 0 ? (
                <CheckSquare className="w-4 h-4 text-[#FF5A00]" />
              ) : (
                <Square className="w-4 h-4" />
              )}
            </button>
            <HeaderCell col="name" label="Cliente" />
            <HeaderCell col="rut" label="RUT" />
            <HeaderCell col="address" label="Dirección" />
            <HeaderCell col="frequency" label="Periodicidad" />
            <HeaderCell col="price_uf" label="Precio UF" align="right" />
            <HeaderCell col="billing_day" label="Día factura" align="right" />
            <div />
          </div>

          {sorted.map((c) => {
            const isChecked = selected.has(c.id);
            return (
              <div
                key={c.id}
                data-testid={`customer-row-${c.id}`}
                onClick={() => navigate(`/admin/clientes/${c.id}`)}
                className={`grid grid-cols-[40px_2.2fr_1fr_1.6fr_0.9fr_0.7fr_0.7fr_60px] gap-2 items-center border-b border-neutral-100 hover:bg-neutral-50 transition-colors cursor-pointer ${c.active ? "" : "opacity-60"} ${isChecked ? "bg-[#FF5A00]/5" : ""}`}
              >
                <button
                  type="button"
                  data-testid={`customer-select-${c.id}`}
                  onClick={(e) => { e.stopPropagation(); toggleOne(c.id); }}
                  className="flex items-center justify-center py-3 text-neutral-500 hover:text-[#FF5A00]"
                >
                  {isChecked ? <CheckSquare className="w-4 h-4 text-[#FF5A00]" /> : <Square className="w-4 h-4" />}
                </button>
                <div className="px-3 py-3 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-sm truncate" title={c.name}>{c.name}</span>
                    <ClassificationBadge value={c.classification || "activo_contrato"} testid={`customer-classification-${c.id}`} />
                    {alertMap[c.id] >= 2 && (
                      <span title={`${alertMap[c.id]} facturas vencidas`} data-testid={`customer-delinquent-${c.id}`} className="font-mono-kpi text-[9px] uppercase tracking-wider bg-[#D50000]/10 text-[#B71C1C] border border-[#D50000]/30 px-1.5 py-0.5 inline-flex items-center gap-0.5">
                        <AlertTriangle className="w-2.5 h-2.5" />Morosa
                      </span>
                    )}
                    {!c.active && (
                      <span className="font-mono-kpi text-[9px] uppercase tracking-wider bg-[#D50000]/10 text-[#B71C1C] border border-[#D50000]/30 px-1.5 py-0.5">Baja</span>
                    )}
                    {c.requires_oc && (
                      <span data-testid={`customer-oc-badge-${c.id}`} className="font-mono-kpi text-[9px] uppercase tracking-wider bg-[#FFC107]/15 text-[#B28C00] border border-[#FFC107]/40 px-1.5 py-0.5 inline-flex items-center gap-0.5">
                        <Receipt className="w-2.5 h-2.5" />OC
                      </span>
                    )}
                  </div>
                  {(c.contacts?.[0]?.name || c.contact_name) && (
                    <div className="text-xs text-neutral-500 truncate">{c.contacts?.[0]?.name || c.contact_name}</div>
                  )}
                </div>
                <div className="px-3 py-3 font-mono-kpi text-xs text-neutral-700">{c.rut || "—"}</div>
                <div className="px-3 py-3 text-xs text-neutral-600 min-w-0">
                  <div className="flex items-start gap-1.5">
                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-neutral-400" />
                    <span className="truncate" title={`${c.address}${c.commune ? ", " + c.commune : ""}`}>
                      {c.address}{c.commune ? `, ${c.commune}` : ""}
                    </span>
                  </div>
                </div>
                <div className="px-3 py-3 text-xs">{freqLabel(c.frequency)}</div>
                <div className="px-3 py-3 text-right font-mono-kpi font-bold text-sm text-[#FF5A00]">
                  {(c.price_uf || 0).toLocaleString("es-CL", { maximumFractionDigits: 2 })}
                </div>
                <div className="px-3 py-3 text-right font-mono-kpi text-xs text-neutral-700">{c.billing_day || "—"}</div>
                <div className="px-3 py-3 flex items-center justify-end gap-1">
                  <button
                    type="button"
                    data-testid={`customer-edit-${c.id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(c); setShowForm(true); }}
                    className="text-neutral-500 hover:text-[#FF5A00] hover:bg-neutral-100 p-1.5"
                    title="Editar"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <ArrowUpRight className="w-3.5 h-3.5 text-neutral-400" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <CustomerForm initial={editing} onClose={() => setShowForm(false)} onSubmit={handleSubmit} />
      )}
      {showImport && <ImportExcelModal onClose={() => setShowImport(false)} onImported={load} />}
    </PageContainer>
  );
}
